/* eslint-disable @typescript-eslint/naming-convention */
import React, { memo, useContext, useEffect, useMemo } from "react";

// components
import { CartDeliverySpeed, CartDeliverySpeedProps } from "@cart-client/components/cart-delivery-speed";
import { DeliverySectionCallout } from "@cart-client/components/delivery-section-renderer/components/DeliverySectionCallout";
import MRODeliverySpeedRestaurantList from "./MRODeliverySpeedRestaurantList";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";

// types
import { MROReviewedCart } from "@cart-client/interfaces/MROFoodCart";

// helpers
import {
    buildRXListIdentifierForMROCart,
    getMROChildCartRestaurantId,
    getMROChildCarts,
    logMROEventsIntoNR,
} from "@cart-client/helpers/MROFoodCart";
import { getDeliverySectionComms } from "@cart-client/components/delivery-section-renderer/helpers";
import { isChildDeliverySpeedDisabled } from "./helpers/mro";
import { format } from "@swiggy-private/core-ui";

// constants
import { MRO_RX_ID_KEY } from "@cart-client/constants/MROConstants";
import { MRO_CART_DELIVERY_SPEED_TEST_IDS } from "@swiggy-private/cart-test-ids";

// styles
import { MRODeliverySpeedContainer, CalloutOnlyWrapper } from "./styles";
import { DELIVERY_SECTION_CALLOUT_VARIANT } from "@cart-client/components/delivery-section-renderer/components/DeliverySectionCallout/constants";

export type MROCartDeliverySpeedProps = CartDeliverySpeedProps;

function MROCartDeliverySpeed(props: MROCartDeliverySpeedProps) {
    const { cartState } = useContext(CartContext);
    const { reviewedCart } = cartState;
    const cartResponse = reviewedCart as MROReviewedCart | null;

    const selected = useMemo(() => getMROChildCarts(cartResponse), [cartResponse]);

    const mroSingleRXCart = selected.length === 1 && selected[0];

    const deliverySpeedSelectionTitle = useMemo(
        () => getDeliverySectionComms(cartResponse)?.delivery_speed_selection_title,
        [cartResponse],
    );

    // logging to nr in case of no selected rx
    useEffect(() => {
        if (selected.length === 0) {
            logMROEventsIntoNR({
                type: "mro_no_selected_carts_for_del_speed",
                selectedLength: selected.length,
                selectedRxIds: buildRXListIdentifierForMROCart(cartResponse),
            });
        }
    }, [selected.length, cartResponse]);

    // Single RX
    if (mroSingleRXCart) {
        const restaurantId = getMROChildCartRestaurantId(mroSingleRXCart);
        return (
            <CartDeliverySpeed
                key={format(MRO_RX_ID_KEY, String(restaurantId))}
                {...props}
                restaurantId={restaurantId}
            />
        );
    }

    // Multi RX
    if (selected.length > 1) {
        // No address and not selected then we show callout only
        const hideRestaurantList = selected.every(isChildDeliverySpeedDisabled);
        const Wrapper = hideRestaurantList ? CalloutOnlyWrapper : React.Fragment;

        return (
            <MRODeliverySpeedContainer data-testid={MRO_CART_DELIVERY_SPEED_TEST_IDS.CONTAINER}>
                <Wrapper>
                    <DeliverySectionCallout
                        message={deliverySpeedSelectionTitle}
                        variant={DELIVERY_SECTION_CALLOUT_VARIANT.TOP_PADDING}
                    />
                </Wrapper>
                {!hideRestaurantList && <MRODeliverySpeedRestaurantList selected={selected} />}
            </MRODeliverySpeedContainer>
        );
    }

    // No selected restaurants, nr log added above
    return null;
}

export default memo(MROCartDeliverySpeed);
